import { GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'
import { FaChartLine, FaSearch, FaTrophy } from 'react-icons/fa'

import { SITE_URL } from '@goodfellas/environment'

import { goodfellasApi } from '../../apis'
import Button from '../../components/button'
import ProfileSearch from '../../components/public-profiles/ProfileSearch'
import { useAuth } from '../../contexts/AuthContext'

export default function SearchHome() {
  const { token } = useAuth()
  const [myProfileId, setMyProfileId] = useState<string | null>(null)

  useEffect(() => {
    const fetchMe = async () => {
      if (!token) return
      try {
        const response = await goodfellasApi.get('/evaluation/resolve/me')
        const id =
          response.data?.id ||
          response.data?.identificator?.uuid ||
          response.data?._id
        if (id) setMyProfileId(id)
      } catch (err) {
        console.debug('Error fetching user info', err)
      }
    }
    fetchMe()
  }, [token])

  return (
    <>
      <Head>
        <title>Comunidade Goodfellas | Explore Perfis de Jiu-Jitsu</title>
        <meta
          name="description"
          content="Explore perfis de atletas de jiu-jitsu, acompanhe estatísticas de competição e faça parte da maior rede de graduados do mundo."
        />
        {/* Conteúdo ainda só em PT: todas as variantes de locale canonizam
            para a URL sem prefixo até a página ser traduzida. */}
        <link
          rel="canonical"
          href={`${SITE_URL.replace(/\/+$/, '')}/search`}
        />
      </Head>

      <div className="min-h-screen bg-gray-950 text-white">
        {/* Hero Section */}
        <div className="relative pt-40 pb-24 md:pt-52 md:pb-40 overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 via-black to-black"></div>
            <div className="absolute top-0 right-0 w-1/2 h-full bg-[url('/images/bg/gf-grad-red.jpg')] bg-cover opacity-10 mix-blend-overlay"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6 leading-tight">
                A Elite do Jiu-Jitsu <br />
                <span className="text-red-500">Conectada</span>
              </h1>
              <p className="text-xl md:text-2xl text-white/70 mb-10 leading-relaxed max-w-2xl">
                Explore perfis de atletas, acompanhe estatísticas de competição
                e faça parte da maior rede de graduados do mundo.
              </p>

              <div className="flex flex-wrap gap-4">
                {token && myProfileId ? (
                  <Button
                    label="Ver Meu Perfil"
                    // Assumindo lutador por padrão, o redirect da página cuidará se for academia
                    href={`/fighters/${myProfileId}`}
                    color="red"
                    size="large"
                  />
                ) : (
                  <Button
                    label="Criar Meu Perfil"
                    href="/avaliacao"
                    color="red"
                    size="large"
                  />
                )}
                <Button
                  label="Explorar Atletas"
                  href="#search"
                  color="transparentNegative"
                  size="large"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions / Features */}
        <div className="container mx-auto px-4 -mt-16 relative z-20 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link href="/search" className="group">
              <div className="bg-gray-900/80 backdrop-blur-xl border border-white/10 p-8 rounded-3xl hover:border-red-500/50 transition-all hover:translate-y-[-4px]">
                <div className="w-14 h-14 bg-red-600/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-red-600 transition-colors">
                  <FaSearch className="text-2xl text-red-500 group-hover:text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Busca Avançada</h3>
                <p className="text-white/50">
                  Encontre atletas por faixa, academia ou categoria de peso em
                  todo o Brasil.
                </p>
              </div>
            </Link>

            <Link href="/avaliacao" className="group">
              <div className="bg-gray-900/80 backdrop-blur-xl border border-white/10 p-8 rounded-3xl hover:border-red-500/50 transition-all hover:translate-y-[-4px]">
                <div className="w-14 h-14 bg-red-600/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-red-600 transition-colors">
                  <FaChartLine className="text-2xl text-red-500 group-hover:text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Estatísticas Reais</h3>
                <p className="text-white/50">
                  Visualize seu aproveitamento, métodos de vitória e evolução
                  técnica detalhada.
                </p>
              </div>
            </Link>

            <Link href="/search" className="group">
              <div className="bg-gray-900/80 backdrop-blur-xl border border-white/10 p-8 rounded-3xl hover:border-red-500/50 transition-all hover:translate-y-[-4px]">
                <div className="w-14 h-14 bg-red-600/20 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-red-600 transition-colors">
                  <FaTrophy className="text-2xl text-red-500 group-hover:text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Rankings</h3>
                <p className="text-white/50">
                  Compare seu desempenho com outros atletas do seu nível e da
                  sua região.
                </p>
              </div>
            </Link>
          </div>

          {/* Section: Search Placeholder (Where functionality would continue) */}
          <div
            id="search"
            className="mt-32 text-center py-20 border-t border-white/5 scroll-mt-32"
          >
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6">
              Pronto para buscar?
            </h2>
            <p className="text-white/40 max-w-xl mx-auto mb-12">
              Utilize nossa ferramenta de busca para encontrar qualquer atleta
              ou academia registrada na plataforma.
            </p>
            <ProfileSearch />
          </div>
        </div>
      </div>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale || 'pt', [
        'common',
        'avaliacao360',
      ])),
    },
  }
}
